import { useRef, useState } from "react";
import { Plus } from "lucide-react";
import { ConversationWordmark } from "../OlliLogo";
import { NO_DATA } from "../../lib/format";
import { money, shortDate } from "./api";
import { useCountUp } from "./parts";

// Design: components/wallet/BalanceCard.tsx.

/** EMV chip in brushed silver */
function Chip() {
  return (
    <span aria-hidden className="relative block h-9 w-12 overflow-hidden rounded-md bg-[image:var(--gradient-chip)] shadow-[inset_0_0_0_1px_rgb(0_0_0/0.18),inset_0_1px_0_rgb(255_255_255/0.8)]">
      {/* ds-ok: chip contact lines */}
      <svg viewBox="0 0 48 36" className="absolute inset-0 h-full w-full" fill="none" stroke="rgb(0 0 0 / 0.28)" strokeWidth="1">
        <path d="M0 12h14M0 24h14M34 12h14M34 24h14M14 0v36M34 0v36M14 18h20" />
        <rect x="18" y="8" width="12" height="20" rx="3" />
      </svg>
    </span>
  );
}

/** Contactless waves */
function Waves() {
  return (
    <svg aria-hidden viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" className="text-ink/70">
      <path d="M8.5 8.5a5 5 0 0 1 0 7" />
      <path d="M12 6a8.5 8.5 0 0 1 0 12" />
      <path d="M15.5 3.5a12 12 0 0 1 0 17" />
    </svg>
  );
}

/**
 * Available balance as a metal card: sun foil, brushed texture, gloss, a light that follows the pointer
 * and a slight tilt. The tilt is dropped when the pointer leaves so the text settles sharp.
 *
 * `stats` = { balance: number|null, daysLeft: number|null (Infinity = no spend), lastRecharge: {amount, ts}|null|undefined (unknown) }
 */
export function BalanceCard({ stats, clinic, onRecharge, style }) {
  const known = stats.balance !== null;
  const shown = useCountUp(known ? stats.balance : 0, 900);
  const [dollars, cents] = known ? money(shown).split(".") : [NO_DATA, null];
  const ref = useRef(null);
  const [tilt, setTilt] = useState(null);

  const onMove = (e) => {
    if (!ref.current || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const r = ref.current.getBoundingClientRect();
    const x = (e.clientX - r.left) / r.width;
    const y = (e.clientY - r.top) / r.height;
    setTilt({ rx: (0.5 - y) * 8, ry: (x - 0.5) * 10, gx: x * 100, gy: y * 100 });
  };

  return (
    <section aria-label="Available balance" style={style} className="col-span-6 animate-nav-in [perspective:1100px]">
      <div
        ref={ref}
        onMouseMove={onMove}
        onMouseLeave={() => setTilt(null)}
        style={tilt ? { transform: `rotateX(${tilt.rx.toFixed(2)}deg) rotateY(${tilt.ry.toFixed(2)}deg)` } : undefined}
        className="relative flex aspect-[1.75] min-h-[248px] w-full flex-col justify-between overflow-hidden rounded-[22px] bg-[image:var(--gradient-foil)] p-6 text-ink shadow-card3d transition-transform duration-200 ease-out [transform-style:preserve-3d]"
      >
        {/* ds-ok: card radius matches a payment card's corner, one-off */}
        {/* Olli's two bubbles, embossed into the metal */}
        <svg aria-hidden viewBox="0 0 200 200" className="pointer-events-none absolute -bottom-16 -right-10 h-[300px] w-[300px] opacity-[0.16]">
          <circle cx="120" cy="80" r="70" fill="none" stroke="var(--color-paper)" strokeWidth="18" />
          <circle cx="70" cy="130" r="52" fill="none" stroke="var(--color-ink)" strokeWidth="14" opacity="0.6" />
        </svg>

        {/* Gloss band sweeps once on load */}
        <span aria-hidden className="pointer-events-none absolute inset-y-0 left-0 w-1/3 -translate-x-[120%] animate-sheen bg-gradient-to-r from-paper/0 via-paper/55 to-paper/0" />

        {/* Light that follows the pointer */}
        <span
          aria-hidden
          className={`pointer-events-none absolute inset-0 transition-opacity duration-300 ${tilt ? "opacity-100" : "opacity-0"}`}
          style={{ background: `radial-gradient(420px circle at ${tilt?.gx ?? 50}% ${tilt?.gy ?? 0}%, rgb(255 255 255 / 0.55), rgb(255 255 255 / 0) 55%)` }}
        />

        <div className="relative flex items-start justify-between">
          <div className="flex flex-col gap-0.5">
            <span className="text-small font-semibold">Available balance</span>
            <span className="text-caption text-ink/65">{clinic}</span>
          </div>
          <ConversationWordmark tone="yellow" className="h-7 w-auto" />
        </div>

        <div className="relative flex items-center gap-4">
          <Chip />
          <Waves />
        </div>

        <div className="relative flex flex-col gap-1">
          <p className="text-display font-semibold tabular-nums leading-none [text-shadow:0_1px_0_rgb(255_255_255/0.6)]" aria-label={money(stats.balance)}>
            {dollars}
            {cents !== null && <span className="text-h1">.{cents}</span>}
          </p>
          <p className="text-small text-ink/70">
            {stats.daysLeft === null
              ? NO_DATA
              : Number.isFinite(stats.daysLeft)
                ? `Lasts about ${stats.daysLeft} days at this month's spend`
                : "No spend this month"}
          </p>
        </div>

        <div className="relative flex items-end justify-between gap-4">
          <span className="text-caption text-ink/70">
            {stats.lastRecharge === undefined
              ? NO_DATA
              : stats.lastRecharge
                ? `Last recharge ${money(stats.lastRecharge.amount)} on ${shortDate(stats.lastRecharge.ts)}`
                : "No recharges yet"}
          </span>
          <button
            onClick={onRecharge}
            className="flex h-11 items-center gap-2 rounded-full bg-ink px-5 text-small font-semibold text-paper shadow-[0_6px_16px_-6px_rgb(0_0_0/0.5)] transition-colors hover:bg-ink/85"
          >
            <Plus size={16} strokeWidth={2} />
            Recharge wallet
          </button>
        </div>
      </div>
    </section>
  );
}
